import { FC, useCallback, useMemo } from "react";

import { useBandContext } from "Bands/contexts";
import { IBandUserWithPermissions } from "Bands/types";
import { usePermissionsGroups } from "Bands/utils";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { copy } from "shared/assets";
import { Button, ButtonContainer, Message, Table, TableColumn } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast/state";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { MutedDarkText, SmallText } from "shared/styles";
import { IPermission, IStatus, ITheme } from "shared/types";
import { getLastIdFromPath } from "shared/utils";
import { useEditPermissions } from "../hooks/useEditPermissions";
import { PermissionCheckbox } from "./PermissionCheckbox";

interface PermissionsTableProps {
  users: IBandUserWithPermissions[];
  handleClose: () => void;
}

interface PermissionChange {
  [userId: string]: {
    permission: string;
    granted: boolean;
  }[];
}

export const PermissionsTableEdit: FC<PermissionsTableProps> = ({ users, handleClose }) => {
  const { rootGroups, childrenGroups } = usePermissionsGroups({
    permissions: users[0].permissions,
  });
  const dispatch = useDispatch();
  const { getCategory } = useBandContext();
  const { editPermissionsQuery } = useEditPermissions();
  const { register, handleSubmit } = useForm();

  const handleSaveChanges = useCallback(
    async data => {
      const changesByUser = Object.keys(data).reduce((acc, change) => {
        const [userId, permissionId] = change.split("#");

        if (!acc[userId]) {
          acc[userId] = [];
        }

        acc[userId].push({
          permission: API_ENDPOINTS.PERMISSION_TYPE(permissionId),
          granted: data[change],
        });

        return acc;
      }, {} as PermissionChange);

      try {
        const promises = Object.entries(changesByUser).map(([userId, permissions]) =>
          editPermissionsQuery.mutateAsync({
            userId,
            data: { permissions },
          })
        );

        await Promise.all(promises);
      } catch (error) {
        console.error(error);
      } finally {
        dispatch(
          addTemporaryToast({
            status: IStatus.success,
            message: copy.permissions.editPermissions.messages.success,
          })
        );
        handleClose();
      }
    },
    [handleClose, editPermissionsQuery, dispatch]
  );

  const renderPermissionCheckbox = useCallback(
    (userId: string, permission: IPermission, allPermissions: IPermission[]) => {
      // Find all dependencies of a permission recursively and build dependency map
      const buildDependencyMap = (allPermissions: IPermission[]): Record<string, string[]> => {
        const dependencyMap: Record<string, string[]> = {};

        const findAllDependencies = (
          permissionId: string,
          visited = new Set<string>()
        ): string[] => {
          if (visited.has(permissionId)) {
            return [];
          }
          visited.add(permissionId);

          const permission = allPermissions.find(
            p => getLastIdFromPath(p.permission.id) === permissionId
          );

          if (!permission?.permission.dependencies) {
            return [];
          }

          const directDependencies = permission.permission.dependencies.map(dep =>
            getLastIdFromPath(dep.id)
          );
          const nestedDependencies = directDependencies.flatMap(depId =>
            findAllDependencies(depId, visited)
          );

          //@ts-ignore
          return [...new Set([...directDependencies, ...nestedDependencies])];
        };

        // Build map for all permissions
        allPermissions.forEach(p => {
          const permId = getLastIdFromPath(p.permission.id);
          dependencyMap[permId] = findAllDependencies(permId);
        });

        return dependencyMap;
      };

      const dependencyMap = buildDependencyMap(allPermissions);
      const permissionId = getLastIdFromPath(permission.permission.id);
      const dependencies = dependencyMap[permissionId] || [];

      return (
        <PermissionCheckbox
          value={permission.id}
          userId={userId}
          name={`${userId}#${permission.permission?.id}`}
          register={register}
          defaultValue={permission.granted}
          dependencies={dependencies.map(id => ({ id }))}
          dependencyMap={dependencyMap}
          allPermissions={allPermissions}
        />
      );
    },
    [register]
  );

  const getRootColumns = useCallback(
    (index: number) => {
      return childrenGroups?.[index]?.map(child => ({
        id: child.id,
        Header: child.label,
        description: child.description,
        columns: (() => {
          const groupPermissions = users[0].permissions.filter(
            perm => perm.permission.permissionGroup?.id === child.id
          );

          if (!groupPermissions) return [];

          return groupPermissions.map(({ permission: { shortLabel, id: permId } }) => ({
            id: permId,
            Header: shortLabel,
            accessor: "permissions",
            Cell: ({ row }: { row: { original: IBandUserWithPermissions } }) => {
              const user = row.original;
              const perm = user.permissions.find(({ permission: { id } }) => id === permId);

              if (!perm) return null;

              return renderPermissionCheckbox(user.id, perm, user.permissions);
            },
          }));
        })(),
      }));
    },
    [childrenGroups, users, renderPermissionCheckbox]
  );

  const userColumn: TableColumn = useMemo(
    () => ({
      Header: "",
      accessor: "firstname",
      Cell: ({ row }) => {
        const invitation = row.original as IBandUserWithPermissions;
        return (
          <Spacer size="xxs">
            <span>
              {invitation.firstname} {invitation.lastname}
            </span>
            <SmallText as={MutedDarkText}>{getCategory(invitation.category?.id)?.label}</SmallText>
          </Spacer>
        );
      },
    }),
    [getCategory]
  );

  const columns = useMemo(() => {
    const rootColumns = rootGroups
      ? rootGroups.map(({ label, id, description }, index) => ({
          id,
          Header: label,
          description: description,
          columns: getRootColumns(index),
        }))
      : [];

    return [userColumn, ...rootColumns];
  }, [rootGroups, userColumn, getRootColumns]);

  return (
    <Spacer size="m">
      <form onSubmit={handleSubmit(handleSaveChanges)}>
        <Spacer size="m" spaceDirection="vertical">
          <Table
            columns={columns as TableColumn[]}
            data={users}
            options={{
              isStriped: false,
              isOutline: true,
              isMultiHeading: true,
            }}
            noScroll
          />
          <ButtonContainer>
            <Button type="submit" theme={ITheme.primary} disabled={editPermissionsQuery.isPending}>
              Set permissions
            </Button>
            <Button theme={ITheme.secondary} onClick={handleClose}>
              Do this later
            </Button>
          </ButtonContainer>
          {editPermissionsQuery.isError && (
            <Message status={IStatus.error}>{copy.errors.general}</Message>
          )}
        </Spacer>
      </form>
    </Spacer>
  );
};
